import React, { useEffect, useState } from "react";
import { Shield, AlertTriangle, TrendingDown, Wallet, Users, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from "recharts";
import { api, formatTL, formatNum, fmtDate } from "@/lib/api";

const RISK_LABEL = {
  yuksek: { text: "Yüksek", color: "bg-rose-100 text-rose-700 border-rose-200", bar: "#DC2626" },
  orta:   { text: "Orta",   color: "bg-amber-100 text-amber-700 border-amber-200", bar: "#F59E0B" },
  dusuk:  { text: "Düşük",  color: "bg-emerald-100 text-emerald-700 border-emerald-200", bar: "#059669" },
};

export default function KidemReserveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/kidem-reserve")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Tazminat rezervi yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400">Rezerv hesaplanıyor...</div>;
  }
  if (!data) return null;

  const chartData = data.rows.map((r) => ({
    name: r.ad_soyad.split(" ")[0], // kısa isim
    full: r.ad_soyad,
    tazminat: r.kesirli_tazminat,
    risk: r.risk,
  }));

  const pieData = [
    { name: "Düşük Risk (<3 yıl)", value: data.risk_summary.dusuk, fill: "#059669" },
    { name: "Orta Risk (3-9 yıl)", value: data.risk_summary.orta, fill: "#F59E0B" },
    { name: "Yüksek Risk (10+ yıl)", value: data.risk_summary.yuksek, fill: "#DC2626" },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-4">
      {/* Hero Alert Band */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-600/20 border border-red-500/40 rounded-lg grid place-items-center">
            <Shield size={22} className="text-red-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Potansiyel Kıdem Tazminatı Yükü</div>
            <div className="font-display text-3xl font-extrabold tracking-tight mt-1" data-testid="total-reserve">
              {formatTL(data.toplam_rezerv)}
            </div>
            <div className="text-sm text-slate-300 mt-1">
              {data.total_personnel} aktif personel · Bugün ({data.today}) tüm personel ayrılırsa ödenecek toplam
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-6 text-right">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Aylık Birikim</div>
            <div className="font-mono-tab text-xl font-bold text-emerald-300 mt-0.5">
              {formatTL(data.toplam_aylik_birikim)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Ort. Kıdem</div>
            <div className="font-mono-tab text-xl font-bold text-amber-300 mt-0.5">
              {data.ortalama_yil} yıl
            </div>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="En Yüksek Tazminat" value={formatTL(data.max_tazminat)} icon={AlertTriangle} tone="rose" />
        <StatCard label="Yüksek Riskli Personel" value={data.risk_summary.yuksek} icon={AlertTriangle} tone="rose" isCount />
        <StatCard label="Orta Riskli Personel" value={data.risk_summary.orta} icon={TrendingDown} tone="amber" isCount />
        <StatCard label="Düşük Riskli Personel" value={data.risk_summary.dusuk} icon={Users} tone="emerald" isCount />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Personnel bar chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
              <Wallet size={18} /> Personel Bazlı Tazminat Yükü
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Günümüz itibarıyla potansiyel toplu ödeme (kesirli yıl bazlı)</p>
          </div>
          <div className="p-4 h-80" data-testid="reserve-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                />
                <Tooltip
                  formatter={(v) => formatTL(v)}
                  labelFormatter={(l, payload) => payload?.[0]?.payload?.full || l}
                  contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC" }}
                />
                <Bar dataKey="tazminat" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, i) => <Cell key={i} fill={RISK_LABEL[entry.risk].bar} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Risk pie */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle size={18} /> Risk Dağılımı
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Kıdem yılına göre risk kategorileri</p>
          </div>
          <div className="p-4 h-80" data-testid="risk-pie">
            {pieData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-slate-400">
                <div className="flex flex-col items-center gap-2"><FileSearch size={28} className="text-slate-300" />Veri yok</div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    outerRadius={80}
                    innerRadius={40}
                    paddingAngle={2}
                    label={({ name, value }) => `${value}`}
                    labelLine={false}
                  >
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v) => `${v} personel`}
                    contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC" }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900">Personel Rezerv Detayı</h3>
          <p className="text-xs text-slate-500 mt-0.5">Tazminat yüküne göre azalan sırada</p>
        </div>
        <div className="sticky-table">
          <table className="w-full text-sm" data-testid="reserve-table">
            <thead>
              <tr className="bg-slate-900 text-slate-100">
                <Th>Personel</Th>
                <Th>İşe Başlama</Th>
                <Th center>Kıdem (Yıl)</Th>
                <Th right>Aylık Brüt</Th>
                <Th right>Aylık Birikim</Th>
                <Th right>Potansiyel Tazminat (Tam Yıl)</Th>
                <Th right>Güncel Tazminat (Kesirli)</Th>
                <Th center>Risk</Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const risk = RISK_LABEL[r.risk];
                return (
                  <tr key={r.personnel_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`reserve-row-${r.sicil_no}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900">{r.ad_soyad}</div>
                      <div className="text-xs text-slate-500">{r.pozisyon} · TC {r.sicil_no}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600 font-mono-tab text-xs">{fmtDate(r.ise_baslama_tarihi)}</td>
                    <td className="px-5 py-3 text-center">
                      <div className="font-mono-tab font-bold text-slate-900">{r.tam_yil}</div>
                      <div className="text-[10px] text-slate-500">({r.kesirli_yil})</div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono-tab text-slate-700">{formatNum(r.aylik_brut)}</td>
                    <td className="px-5 py-3 text-right font-mono-tab text-emerald-700 font-semibold">{formatNum(r.aylik_birikim)}</td>
                    <td className="px-5 py-3 text-right font-mono-tab font-semibold text-slate-900">{formatTL(r.potansiyel_tazminat)}</td>
                    <td className="px-5 py-3 text-right font-mono-tab font-bold text-rose-700">{formatTL(r.kesirli_tazminat)}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${risk.color}`}>
                        {risk.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold">
                <td className="px-5 py-3" colSpan={4}>TOPLAM ({data.total_personnel} personel)</td>
                <td className="px-5 py-3 text-right font-mono-tab text-emerald-700">{formatTL(data.toplam_aylik_birikim)}</td>
                <td className="px-5 py-3 text-right font-mono-tab" colSpan={1}>—</td>
                <td className="px-5 py-3 text-right font-mono-tab text-rose-700">{formatTL(data.toplam_rezerv)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        <strong>Hesaplama Notu:</strong> Potansiyel tazminat = aylık brüt × tam kıdem yılı (Türkiye standart kıdem tazminatı).
        Güncel kesirli tazminat, ay bazında orantılı hesaplanır. Aylık birikim = brüt / 12 (her ay 1/12 brüt birikir).
        SGK tazminat tavanı dikkate alınmamıştır — bu bir karşılık (rezerv) tahminidir.
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, isCount }) {
  const map = {
    rose: { bg: "bg-rose-50", text: "text-rose-700", icon: "bg-rose-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-700", icon: "bg-amber-600" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "bg-emerald-600" },
  };
  const t = map[tone] || map.emerald;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</div>
          <div className={`font-mono-tab text-xl font-bold ${t.text}`}>{isCount ? value : value}</div>
        </div>
        <div className={`${t.icon} text-white w-9 h-9 rounded-lg grid place-items-center`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function Th({ children, right, center }) {
  const align = right ? "text-right" : center ? "text-center" : "text-left";
  return <th className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${align}`}>{children}</th>;
}
